import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Utensils, Wine, LayoutList, Plus, Pencil, Trash2, ChevronRight, CalendarClock, GripVertical, Eye, EyeOff, FileText, FileUp, Languages, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { SortableList, SortableRow } from "@/components/SortableList";
import ImageUpload from "@/components/ImageUpload";
import PdfImportDialog from "@/pages/admin/PdfImportDialog";
import PdfUpdateDialog from "@/pages/admin/PdfUpdateDialog";
import { toast } from "sonner";

const TYPE_META = {
  food: { label: "Food", icon: Utensils },
  bevande: { label: "Bevande", icon: Wine },
  misto: { label: "Misto", icon: LayoutList },
};

const LANG_META = {
  it: { label: "Italiano", flag: "🇮🇹" }, en: { label: "English", flag: "🇬🇧" },
  fr: { label: "Français", flag: "🇫🇷" }, de: { label: "Deutsch", flag: "🇩🇪" },
  es: { label: "Español", flag: "🇪🇸" }, pt: { label: "Português", flag: "🇵🇹" },
  hr: { label: "Hrvatski", flag: "🇭🇷" }, hu: { label: "Magyar", flag: "🇭🇺" },
  nl: { label: "Nederlands", flag: "🇳🇱" }, pl: { label: "Polski", flag: "🇵🇱" },
  ro: { label: "Română", flag: "🇷🇴" }, ru: { label: "Русский", flag: "🇷🇺" },
  sk: { label: "Slovenčina", flag: "🇸🇰" },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { restaurant } = useOutletContext() || {};
  const [menus, setMenus] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ name: "", type: "food", is_daily: false, visible: true, cover_image: null, link_type: "internal", link_url: "", link_pdf: null });
  const [loading, setLoading] = useState(true);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [updateMenuId, setUpdateMenuId] = useState(null);
  const [linkPdfUploading, setLinkPdfUploading] = useState(false);

  const langs = restaurant?.settings?.languages || ["it"];

  const openPreview = (code) => {
    if (!restaurant?.slug) return;
    window.open(`/m/${restaurant.slug}?lang=${code}`, "_blank", "noopener,noreferrer");
  };

  const load = () => api.get("/menus").then((r) => { setMenus(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ name: "", type: "food", is_daily: false, visible: true, cover_image: null, link_type: "internal", link_url: "", link_pdf: null }); setDialog({ mode: "create" }); };
  const openEdit = (m) => { setForm({ name: m.name, type: m.type, is_daily: m.is_daily, visible: m.visible !== false, cover_image: m.cover_image || null, link_type: m.link_type || "internal", link_url: m.link_url || "", link_pdf: m.link_pdf || null }); setDialog({ mode: "edit", menu: m }); };

  const uploadLinkPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLinkPdfUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, link_pdf: res.data.path }));
      toast.success("PDF caricato");
    } catch (err) { toast.error("Errore nel caricamento del PDF"); }
    finally { setLinkPdfUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Inserisci un nome");
    if (form.link_type === "external" && !(form.link_url || "").trim()) return toast.error("Inserisci l'URL del link esterno");
    if (form.link_type === "pdf" && !form.link_pdf) return toast.error("Carica il file PDF");
    const payload = {
      name: form.name, type: form.type, is_daily: form.is_daily, visible: form.visible, cover_image: form.cover_image,
      link_type: form.link_type,
      link_url: form.link_type === "external" ? (form.link_url || "").trim() : null,
      link_pdf: form.link_type === "pdf" ? form.link_pdf : null,
    };
    try {
      if (dialog.mode === "create") await api.post("/menus", payload);
      else await api.put(`/menus/${dialog.menu.id}`, payload);
      toast.success("Salvato");
      setDialog(null);
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminare "${m.name}" e tutti i suoi piatti?`)) return;
    await api.delete(`/menus/${m.id}`);
    toast.success("Menù eliminato");
    load();
  };

  const reorder = async (ids, next) => { setMenus(next); await api.put("/menus/reorder", { ids }); };

  const toggleVisible = async (m) => {
    const next = m.visible === false;
    setMenus(menus.map((x) => (x.id === m.id ? { ...x, visible: next } : x)));
    try {
      await api.put(`/menus/${m.id}/visibility`, { visible: next });
      toast.success(next ? "Menù visibile nel pubblico" : "Menù nascosto dal pubblico");
    } catch (e) {
      setMenus(menus);
      toast.error("Errore");
    }
  };

  if (loading) return <p className="text-muted-foreground">Caricamento…</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="font-cabinet font-bold text-3xl tracking-tight">I tuoi menù</h1>
          <p className="text-muted-foreground text-sm mt-1">Trascina per riordinare. Usa l'occhio per mostrare o nascondere un menù dal menù pubblico.</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="preview-lang-btn" className="rounded-full border border-border text-foreground font-semibold px-4 py-2.5 flex items-center gap-2 hover:bg-muted transition-colors"><Languages className="w-4 h-4" /> <span className="hidden sm:inline">Anteprima</span></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52" data-testid="preview-lang-menu">
              <DropdownMenuLabel>Anteprima menù pubblico</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {langs.map((code) => {
                const meta = LANG_META[code] || { label: code.toUpperCase(), flag: "🏳️" };
                return (
                  <DropdownMenuItem key={code} onClick={() => openPreview(code)} data-testid={`preview-lang-${code}`} className="cursor-pointer gap-2">
                    <span className="text-lg leading-none">{meta.flag}</span> {meta.label}{code === "it" && " (originale)"}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={() => setPdfOpen(true)} data-testid="import-pdf-btn" className="rounded-full border border-[#002FA7] text-[#002FA7] font-semibold px-4 py-2.5 flex items-center gap-2 hover:bg-[#002FA7]/5"><FileText className="w-4 h-4" /> <span className="hidden sm:inline">Importa da PDF</span></button>
          <button onClick={openCreate} data-testid="add-menu-btn" className="rounded-full bg-[#002FA7] text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:bg-[#00248a] transition-colors">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nuovo menù</span>
          </button>
        </div>
      </div>
      {pdfOpen && <PdfImportDialog open={pdfOpen} menus={menus} onClose={() => setPdfOpen(false)} onApplied={() => { setPdfOpen(false); load(); }} />}
      {updateMenuId && <PdfUpdateDialog open menuId={updateMenuId} client={api} base="/menus" onClose={() => setUpdateMenuId(null)} onApplied={() => { setUpdateMenuId(null); load(); }} />}

      <SortableList
        items={menus}
        onReorder={reorder}
        className="grid gap-3"
        renderItem={(m) => {
          const Meta = TYPE_META[m.type] || TYPE_META.food;
          return (
            <SortableRow key={m.id} id={m.id}>
              {({ listeners }) => (
                <div data-testid={`menu-item-${m.id}`} className={`bg-white rounded-2xl border border-border p-4 flex items-center gap-3 hover:shadow-sm transition-shadow ${m.visible === false ? "opacity-60" : ""}`}>
                  <button {...listeners} className="drag-handle p-1 text-muted-foreground touch-none" data-testid={`drag-menu-${m.id}`}><GripVertical className="w-5 h-5" /></button>
                  <div className={`w-11 h-11 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 ${m.is_daily ? "bg-amber-100 text-amber-700" : "bg-[#002FA7]/10 text-[#002FA7]"}`}>
                    {m.is_daily ? <CalendarClock className="w-5 h-5" /> : <Meta.icon className="w-5 h-5" />}
                  </div>
                  <button onClick={() => navigate(`/admin/menu/${m.id}`)} className="flex-1 text-left min-w-0" data-testid={`open-menu-${m.id}`}>
                    <div className="flex items-center gap-2">
                      <p className={`font-semibold truncate ${m.visible === false ? "line-through" : ""}`}>{m.name}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{m.visible === false ? "Nascosto dal menù pubblico" : (m.is_daily ? "Menù del giorno" : Meta.label)}</p>
                  </button>
                  <button onClick={() => toggleVisible(m)} data-testid={`toggle-menu-visible-${m.id}`} title={m.visible === false ? "Rendi visibile" : "Nascondi"} className="p-2 rounded-lg hover:bg-muted text-muted-foreground">{m.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  <button onClick={() => setUpdateMenuId(m.id)} data-testid={`update-pdf-${m.id}`} title="Aggiorna da PDF" className="p-2 rounded-lg hover:bg-muted text-muted-foreground"><FileUp className="w-4 h-4" /></button>
                  <button onClick={() => openEdit(m)} data-testid={`edit-menu-${m.id}`} className="p-2 rounded-lg hover:bg-muted text-muted-foreground"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(m)} data-testid={`delete-menu-${m.id}`} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                  <ChevronRight className="w-5 h-5 text-muted-foreground hidden sm:block" />
                </div>
              )}
            </SortableRow>
          );
        }}
      />
      {menus.length === 0 && <p className="text-muted-foreground text-center py-10">Nessun menù. Creane uno!</p>}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="menu-dialog">
          <DialogHeader><DialogTitle>{dialog?.mode === "create" ? "Nuovo menù" : "Modifica menù"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nome (testo del bottone in home)</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="menu-name-input" placeholder="Es. Pranzo, Drink List…" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
            </div>
            <div>
              <label className="text-sm font-medium">Tipo</label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1" data-testid="menu-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="food">Food (cibo)</SelectItem>
                  <SelectItem value="bevande">Bevande (bar)</SelectItem>
                  <SelectItem value="misto">Misto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ImageUpload value={form.cover_image} onChange={(p) => setForm({ ...form, cover_image: p })} label="Foto di copertina (card in home)" testid="menu-cover" />

            <div className="rounded-xl border border-border p-3 space-y-3" data-testid="menu-destination">
              <div>
                <label className="text-sm font-medium">Destinazione della card in home</label>
                <Select value={form.link_type} onValueChange={(v) => setForm({ ...form, link_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="menu-link-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Vista interna (menù della piattaforma)</SelectItem>
                    <SelectItem value="external">Link esterno (URL)</SelectItem>
                    <SelectItem value="pdf">File PDF</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Di default la card apre la vista interna con categorie e piatti. Puoi invece farla puntare a un link esterno o a un PDF.</p>
              </div>
              {form.link_type === "external" && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">URL del link</label>
                  <input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} data-testid="menu-link-url-input" placeholder="https://esempio.it/il-mio-menu" className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" />
                </div>
              )}
              {form.link_type === "pdf" && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">File PDF</label>
                  {form.link_pdf ? (
                    <div className="mt-1 flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid="menu-link-pdf-current">
                      <FileText className="w-4 h-4 text-[#002FA7]" />
                      <span className="flex-1 truncate text-muted-foreground">PDF caricato</span>
                      <button type="button" onClick={() => setForm({ ...form, link_pdf: null })} data-testid="menu-link-pdf-remove" className="text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <label className="mt-1 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border py-3 text-sm cursor-pointer hover:border-[#002FA7]" data-testid="menu-link-pdf-dropzone">
                      {linkPdfUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</> : <><FileText className="w-4 h-4" /> Seleziona un PDF</>}
                      <input type="file" accept="application/pdf" className="hidden" onChange={uploadLinkPdf} data-testid="menu-link-pdf-input" disabled={linkPdfUploading} />
                    </label>
                  )}
                </div>
              )}
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.is_daily} onChange={(e) => setForm({ ...form, is_daily: e.target.checked })} data-testid="menu-daily-checkbox" className="w-4 h-4 accent-[#002FA7]" />
              <span className="text-sm">Menù del giorno (aggiornamento rapido)</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="menu-visible-checkbox" className="w-4 h-4 accent-[#002FA7]" />
              <span className="text-sm">Visibile nel menù pubblico</span>
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setDialog(null)} className="rounded-full px-5 py-2 text-sm font-medium hover:bg-muted">Annulla</button>
            <button onClick={save} data-testid="menu-save-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a]">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
